import type {
  CartItem,
  PhotoBookCartItem,
  TilesCartItem,
} from 'stores/cartStore/CartItem'
import type { DiscountInfo } from 'features/cart/hooks/useDiscountInfo'
import type { GroupPricingResult } from 'features/cart/groupCheckoutPricing.types'
import { getBookPriceTier } from './photoBooksAnalytics'
import { convertPriceToUSDCurrency, roundDecimal } from 'utils/utils'
import sumBy from 'lodash/sumBy'
import uniq from 'lodash/uniq'

export function getCartItemContext(item: CartItem) {
  return {
    'Product ID': item.id,
    'Product Type': item.productType,
    'Warning Count': item.warnings?.length ?? 0,
    'Warning Types': item.warnings?.map((w) => w.type),
    'Dismissed Warning Count': item.dismissedWarningTypes?.length ?? 0,
    'Dismissed Warning Types': item.dismissedWarningTypes,
  }
}

export function getCartItemsMetrics({
  tilesItem,
  photoBookItems,
  isPriceTierEnabled,
}: {
  tilesItem: TilesCartItem | undefined
  photoBookItems: PhotoBookCartItem[]
  isPriceTierEnabled: boolean
}) {
  const items: CartItem[] = [
    ...(tilesItem ? [tilesItem] : []),
    ...photoBookItems,
  ]
  const productTypes = uniq(items.map((item) => item.productType))

  return {
    'Cart Items Count': items.length,
    'Tiles Count': tilesItem?.metadata.tilesCount ?? 0,
    'Photo Books Count': sumBy(photoBookItems, (item) => item.quantity),
    'Distinct Photo Books Count': photoBookItems.length,
    ...(isPriceTierEnabled && {
      'Price Tier': photoBookItems.map((item) =>
        getBookPriceTier(item.metadata.size, item.metadata.photosCount)
      ),
    }),
    'Cart Items - Product IDs': items.map((item) => item.id),
    'Cart Items - Product Types': items.map((item) => item.productType),
    'Product Types Ordered': productTypes,
    'Product Types Count': productTypes.length,
    'Is Mixed Products': productTypes.length > 1,
  }
}

export function getCartValueMetrics(pricingData: GroupPricingResult) {
  return {
    'Cart Value in USD - Original': convertPriceToUSDCurrency(
      pricingData.productTotalBeforeDiscount,
      pricingData.currency
    ),
    'Cart Value in USD - Actual': convertPriceToUSDCurrency(
      pricingData.productTotalAfterDiscount,
      pricingData.currency
    ),
  }
}

export function getDiscountMetrics({
  discountInfo,
  pricingData,
  promocode,
}: {
  discountInfo: DiscountInfo | null
  pricingData: GroupPricingResult | undefined
  promocode: string | null
}) {
  const hasFreeShipping = pricingData
    ? Object.values(pricingData.shippingOptions).some(
        (option) => option.isEligibleFreeShipping
      )
    : undefined

  let discountSavings: number | undefined
  let discountPercent: number | undefined
  if (pricingData) {
    const totalBeforeDiscount = pricingData.productTotalBeforeDiscount
    const totalAfterDiscount = pricingData.productTotalAfterDiscount
    discountSavings = Math.max(totalBeforeDiscount - totalAfterDiscount, 0)
    discountPercent =
      totalBeforeDiscount > 0
        ? roundDecimal((discountSavings / totalBeforeDiscount) * 100, 2)
        : 0
  }
  const appliedDiscountType = promocode
    ? 'promocode'
    : discountInfo?.applied
    ? 'volume_discount'
    : 'none'

  const buyoverTiers = pricingData?.buyoverData?.tiers
  const appliedTierCount = buyoverTiers?.filter((tier) => tier.applied).length

  return {
    'Discount Progress State': discountInfo
      ? discountInfo.applied
        ? 'reached'
        : 'in_progress'
      : 'discount_info_missing',
    'Cart Discount Type': discountInfo?.type ?? null,
    'Buyover Tier Count': buyoverTiers?.length ?? null,
    'Applied Tier Index':
      appliedTierCount === undefined
        ? null
        : appliedTierCount > 0
        ? appliedTierCount - 1
        : null,
    'Has Free Shipping': hasFreeShipping,
    'Coupon Code': promocode,
    'Has Promo Code': !!promocode,
    'Applied Discount Type': appliedDiscountType,
    'Discount Percent': discountPercent,
    'Discount Amount in USD':
      pricingData && discountSavings !== undefined
        ? convertPriceToUSDCurrency(discountSavings, pricingData.currency)
        : undefined,
  }
}

export function getBagAnalytics({
  tilesItem,
  photoBookItems,
  pricingData,
  discountInfo,
  promocode,
  isPriceTierEnabled,
}: {
  tilesItem: TilesCartItem | undefined
  photoBookItems: PhotoBookCartItem[]
  pricingData: GroupPricingResult | undefined
  discountInfo: DiscountInfo | null
  promocode: string | null
  isPriceTierEnabled: boolean
}) {
  return {
    ...getCartItemsMetrics({ tilesItem, photoBookItems, isPriceTierEnabled }),
    'Pricing Loaded': !!pricingData,
    ...(pricingData ? getCartValueMetrics(pricingData) : {}),
    ...getDiscountMetrics({ discountInfo, pricingData, promocode }),
  }
}
